import {
  CheckCircleFilled,
  EyeOutlined,
  FileDoneOutlined,
} from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from 'antd'
import { useState } from 'react'
import { toast } from 'sonner'

import { type CardfileData } from '@/api/cardfile/cardfileApi'
import { cardfileQueries } from '@/api/cardfile/cardfileQueries'
import {
  type ExistingOrganisation,
  organisationsApi,
} from '@/api/organisations/organisationsApi'
import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import CardfileDetails from '@/components/Cardfile/CardfileDetails/CardfileDetails'
import {
  hasCardfileInfo,
  isCompleteVatNumber,
} from '@/components/Cardfile/lib/cardfileToLegalFields'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import cn from './VatLookup.module.scss'

type VatLookupProps = {
  vatNumber: string
  onApply: (data: CardfileData) => void
  checkUniqueness?: boolean
  isBranch?: boolean
  onVerified?: (parentOrganisationId?: string) => void
  onDuplicate?: (message: string) => void
}

const DUPLICATE_MESSAGE = 'Измените УНП. Организация с таким номером уже есть.'

function VatLookup({
  vatNumber,
  onApply,
  checkUniqueness = true,
  isBranch = false,
  onVerified,
  onDuplicate,
}: VatLookupProps) {
  const queryClient = useQueryClient()
  const [modal, contextHolder] = Modal.useModal()
  const [branchCandidate, setBranchCandidate] =
    useState<ExistingOrganisation | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const uniquenessCheck = useMutation({
    mutationFn: (vat: string) => organisationsApi.checkVatUniqueness(vat),
  })
  const cardfileLoad = useMutation({
    mutationFn: (vat: string) =>
      queryClient.fetchQuery(cardfileQueries.info(vat)),
  })

  const showError = (error: unknown) => handleApiError(error, modal)

  const warnIfEmpty = (data: CardfileData) => {
    if (!hasCardfileInfo(data))
      toast.warning('В картотеке нет данных по этому УНП', {
        description: 'Проверьте УНП или заполните реквизиты вручную',
      })
  }

  const handleRequest = () => {
    if (!checkUniqueness) {
      cardfileLoad.mutate(vatNumber, {
        onSuccess: warnIfEmpty,
        onError: showError,
      })
      return
    }
    uniquenessCheck.mutate(vatNumber, {
      onSuccess: ({ isUnique, existingOrganisation }) => {
        if (isUnique) {
          onVerified?.()
          cardfileLoad.mutate(vatNumber, {
            onSuccess: warnIfEmpty,
            onError: showError,
          })
          return
        }
        if (existingOrganisation) {
          setBranchCandidate(existingOrganisation)
          return
        }
        onDuplicate?.(`Организация с УНП [${vatNumber}] уже существует.`)
      },
      onError: showError,
    })
  }

  const handleBranchConfirm = () => {
    if (!branchCandidate) return
    onVerified?.(branchCandidate.id)
    cardfileLoad.mutate(vatNumber, {
      onSuccess: warnIfEmpty,
      onError: showError,
      onSettled: () => setBranchCandidate(null),
    })
  }

  const handleBranchClose = () => {
    if (!cardfileLoad.isPending) setBranchCandidate(null)
  }

  const handleBranchReject = () => {
    setBranchCandidate(null)
    onDuplicate?.(DUPLICATE_MESSAGE)
  }

  const cardfile =
    cardfileLoad.data && hasCardfileInfo(cardfileLoad.data)
      ? cardfileLoad.data
      : undefined
  const isRequesting =
    uniquenessCheck.isPending ||
    (cardfileLoad.isPending && branchCandidate === null)

  return (
    <>
      {contextHolder}
      {isBranch && (
        <div className={cn.branchPlate}>
          <CheckCircleFilled aria-hidden />
          Филиал существующей организации
        </div>
      )}
      {cardfile ? (
        <div className={cn.actions}>
          <AtomButton
            className={cn.applyEffects}
            icon={<FileDoneOutlined />}
            onClick={() => onApply(cardfile)}
          >
            Подставить данные
          </AtomButton>
          <AtomButton
            variant="secondary"
            className={cn.previewEffects}
            icon={<EyeOutlined />}
            onClick={() => setPreviewOpen(true)}
          >
            Предпросмотр данных
          </AtomButton>
        </div>
      ) : (
        <AtomButton
          fullWidth
          disabled={!isCompleteVatNumber(vatNumber)}
          loading={isRequesting}
          onClick={handleRequest}
        >
          Запросить данные из картотеки
        </AtomButton>
      )}

      <Modal
        open={branchCandidate !== null}
        title="Организация уже существует"
        onCancel={handleBranchClose}
        maskClosable={!cardfileLoad.isPending}
        closable={!cardfileLoad.isPending}
        centered
        width={440}
        footer={
          <div className={cn.branchFooter}>
            <AtomButton
              variant="secondary"
              fullWidth
              disabled={cardfileLoad.isPending}
              onClick={handleBranchReject}
            >
              Нет
            </AtomButton>
            <AtomButton
              fullWidth
              loading={cardfileLoad.isPending}
              onClick={handleBranchConfirm}
            >
              Да, это филиал
            </AtomButton>
          </div>
        }
      >
        <p className={cn.branchQuestion}>
          Организация{' '}
          <b>
            «{branchCandidate?.fullLegalName || branchCandidate?.shortLegalName}
            »
          </b>{' '}
          с таким УНП уже есть. Это её филиал?
        </p>
      </Modal>

      {cardfile && (
        <Modal
          open={previewOpen}
          title={`Данные из картотеки · УНП ${vatNumber}`}
          onCancel={() => setPreviewOpen(false)}
          footer={
            <div className={cn.previewFooter}>
              <AtomButton
                variant="secondary"
                onClick={() => setPreviewOpen(false)}
              >
                Закрыть
              </AtomButton>
              <AtomButton
                icon={<FileDoneOutlined />}
                onClick={() => {
                  onApply(cardfile)
                  setPreviewOpen(false)
                }}
              >
                Подставить данные
              </AtomButton>
            </div>
          }
          width={800}
          centered
        >
          <CardfileDetails data={cardfile} />
        </Modal>
      )}
    </>
  )
}

export default VatLookup
